// FILE: InteractiveGettingStartedGuide.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Card,
    CardContent,
    Avatar,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Fade,
    Zoom,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
    Stack,
    Divider,
    Badge
} from "@mui/material";
import {
    School as SchoolIcon,
    Subject as SubjectIcon,
    Assessment as AssessmentIcon,
    Comment as CommentIcon,
    PlayArrow as PlayIcon,
    VideoLibrary as VideoIcon,
    CheckCircle as CheckIcon,
    RadioButtonUnchecked as UncheckedIcon,
    Lightbulb as TipIcon,
    Visibility as ViewIcon,
    Add as AddIcon,
    Save as SaveIcon,
    FilterList as FilterIcon,
    AutoAwesome as GenerateIcon,
    Close as CloseIcon,
    NavigateNext as NextIcon,
    NavigateBefore as PrevIcon,
    Home as HomeIcon,
    Help as QuestionIcon,
    Timeline as TimelineIcon,
    EmojiEvents as TrophyIcon,
    DragIndicator as DragIcon,
    ExitToApp as ExitIcon,
    Minimize as MinimizeIcon,
    CropFree as ExpandIcon
} from "@mui/icons-material";
import useTutorialVideos from './useTutorialVideos';

const InteractiveGettingStartedGuide = ({ onComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getVideoByTitle } = useTutorialVideos();
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [showDemo, setShowDemo] = useState(false);
    const [currentDemo, setCurrentDemo] = useState(null);
    const [currentVideoTitle, setCurrentVideoTitle] = useState(null);
    const [progress, setProgress] = useState(0);

    // Dragging functionality
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isCollapsed, setIsCollapsed] = useState(false);
    const dialogRef = React.useRef(null);

    const steps = [
        {
            label: "Welcome to Gradely",
            icon: <HomeIcon />,
            color: theme.palette.primary.main,
            content: {
                title: "This Tutorial will teach you basics in no time!",
                description: "At the end of this tutorial you will have your class room setup!.",
                tips: [
                    "This tutorial takes approximately 20 minutes to complete",
                    "You can keep this tutorial to follow instructions",
                    "Each step builds upon the previous one",
                    "You can exit the tutorial anytime by clicking 'Exit Tutorial'"
                ],
                action: "Let's Get Started!",
                visual: "welcome"
            }
        },
        {
            label: "Step 1: Adding Students",
            icon: <SchoolIcon />,
            color: '#4CAF50',
            content: {
                title: "Start by adding your students to create personalized learning profiles.",
                substeps: [
                    { text: "Navigate to Students in the left-hand panel." },
                    { text: "Select Class Level, Section, and Academic Year" },
                    { text: "Click 'View Student List' to see current list of students" },
                    { text: "Enter the student details." },
                    { text: "Click 'Save Changes' to complete the process." }
                ],
                tips: [
                    "💡 You can add multiple students by clicking 'Add Student' multiple times.",
                    "💡 Student information is kept secure and private. Student’s name will not be shared with the AI.",
                    "💡 You can delete students any time. Deleting student will delete all their records.",

                ],
                action: "Adding Students",
                visual: "students",
                videoTitle: "Adding Students"
            }
        },
        {
            label: "Step 2: Adding Subjects, Units & Creating DOK Skills",
            icon: <SubjectIcon />,
            color: '#4CAF50',
            content: {
                title: "Create subjects, units, and DOK Skills.",
                substeps: [
                    { text: "Create Subject(s)", isSubheading: true },
                    { text: "Navigate to Subjects in the left-hand panel." },
                    { text: "Go to the Subjects tab." },
                    { text: "Create subject(s) by following the on-screen instructions." },
                    { text: "Create Unit(s)", isSubheading: true },
                    { text: "Go to the Units tab." },
                    { text: "Add unit(s) by following the on-screen instructions." },
                    { text: "Create Depth of Knowledge (DOK) Skills", isSubheading: true },
                    { text: "Go to the DOK tab." },
                    { text: "Create DOK skills either manually or with the help of AI, as guided by the instructions." }
                ],
                tips: [
                    "💡 You can delete subject by clicking the bin icon. Deleting the subject will delete any DOK records associated with the subject and any units related to the subjects.",
                    "💡 You can delete unit by clicking the bin icon. Deleting the unit will delete any DOK records associated with the unit.",
                    "💡 AI can help generate age-appropriate DOK skill statements. The DOK skills generated are editable. AI will only consider these DOK skills plus your recorded assessment when it generates a detailed feedback comment.",

                ],
                actions: [
                    { text: "Adding Subjects", videoTitle: "Creating Subjects" },
                    { text: "Adding Units", videoTitle: "Adding Units" },
                    { text: "Creating DOK Skills", videoTitle: "Creating DOK Skills" }
                ],
                visual: "curriculum"
            }
        },
        {
            label: "Step 3: Recording Your Assessments",
            icon: <AssessmentIcon />,
            color: '#4CAF50',
            content: {
                title: "Track student progress using DOK levels and proficiency scales.",
                substeps: [
                    { text: "Assess a Single Student", isSubheading: true },
                    { text: "Navigate to Assessment in the left-hand panel." },
                    { text: "Open the Assess Single Student tab." },
                    { text: "Select all required fields: Student, Subject, Unit, and Term." },
                    { text: "Record the DOK level and Proficiency Scale achieved by the student in the unit." },
                    { text: "Click 'Save DOK Report' to save your assessment", icon: <SaveIcon /> },
                    { text: "Assess Multiple Students", isSubheading: true },
                    { text: "Open the Assess Multiple Students tab." },
                    { text: "Select multiple students, subject, unit and term." },
                    { text: "Record the DOK level and Proficiency Scale for each student." },
                    { text: "Click Save DOK Reports.", icon: <SaveIcon /> }
                ],
                tips: [
                    "💡 DOK skills edited in section 3 will only apply to the selected student. This feature allows teachers to carry out differentiated assessment.",
                    "💡 Section 4 can be used to provide your personal comments that will show up on student's report card.",

                ],
                actions: [
                    { text: "Watch: Single Student Assessment", videoTitle: "Recording assessment for single student" },
                    { text: "Watch: Multiple Students Assessment", videoTitle: "Recording assessment for multiple students" }
                ],
                visual: "assessment"
            }
        },
        {
            label: "Step 4:Generating Comments",
            icon: <CommentIcon />,
            color: '#4CAF50',
            content: {
                title: "Create personalized, detailed feedback using advanced AI.",

                substeps: [

                    { text: "Generate Comment for a Single Student", isSubheading: true },

                    { text: "On the Assessment page, open the Generate Comment for a Single Student tab." },
                    { text: "Filter the students by Class level, Section and Academic Year. Select Student, Subject, and the Term." },
                    { text: "All units will be displayed. You may exclude any unit from the comment generation." },
                    { text: "Select the student’s overall assessment." },
                    { text: "Add any optional notes or comments you’d like the AI to include." },
                    { text: "Click Generate Comment." },
                    { text: "Click Save Comment to finish." },
                    { text: "Generate Comments for Multiple Students", isSubheading: true },
                    { text: "On the Assessment page, open the Generate Comment for Multiple Students tab." },
                    { text: "Filter students by Class Level, Section, and Academic Year." },
                    { text: "Select up to 10 students." },
                    { text: "Choose the Subject, Unit, and Term." },
                    { text: "Click Retrieve DOK Assessment." },
                    { text: "Assign an overall assessment to each selected student." },
                    { text: "Click Generate Comments." },
                    { text: "Click Save Comments to finish." },


                ],
                tips: [
                    "💡 Your comment in the Additional Instructions textbox will be considered by the AI when generating a comment. This option is not available for when you are generating a comment for multiple students.",
                    "💡 Do note that for privacy reasons, the AI will not generate comments mentioning student’s name.",
                    "💡 The option to de-select units allows you to have the AI generate a comment based on any number of units.",

                ],
                actions: [
                    { text: "Watch: Single Student Comments", videoTitle: "Generating Comments-Single Student" },
                    { text: "Watch: Multiple Students Comments", videoTitle: "Generating Comments-Multiple Students" }
                ],
                visual: "comments"
            }
        }

    ];

    useEffect(() => {
        // Calculate progress: 7 steps total, each step is ~14.3%
        const newProgress = ((activeStep + 1) / steps.length) * 100;
        setProgress(Math.min(newProgress, 100)); // Cap at 100%
    }, [activeStep, steps.length]);

    const handleNext = () => {
        setCompletedSteps(prev => new Set([...prev, activeStep]));
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepClick = (stepIndex) => {
        setActiveStep(stepIndex);
    };

    const handleDemo = (demoType) => {
        setCurrentDemo(demoType);
        setShowDemo(true);
    };

    const handleVideoDemo = (videoTitle) => {
        setCurrentDemo('video');
        setCurrentVideoTitle(videoTitle);
        setShowDemo(true);
    };

    const VisualDemo = ({ type, step }) => {
        console.log('VisualDemo called with:', { type, step }); // Debug log
        const demoContent = {
            welcome: {
                title: "Gradely Platform Overview",
                content: (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.primary.light }}>
                                <TrophyIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                                <Typography variant="h6" fontWeight="bold">AI-Powered</Typography>
                                <Typography variant="body2">Advanced AI generates personalized comments</Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.secondary.light }}>
                                <TimelineIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                                <Typography variant="h6" fontWeight="bold">Progress Tracking</Typography>
                                <Typography variant="body2">Monitor student growth over time</Typography>
                            </Card>
                        </Grid>
                    </Grid>
                )
            },
            students: {
                title: "Adding Students Tutorial",
                content: step && step.videoTitle ? (() => {
                    const videoData = getVideoByTitle(step.videoTitle);
                    console.log('Video URL being used:', videoData?.videoUrl); // Debug log

                    return videoData && videoData.videoUrl ? (
                        <Box sx={{ width: '100%', height: '80vh' }}>
                            <iframe
                                width="100%"
                                height="100%"
                                src={videoData.videoUrl}
                                title={videoData.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                                style={{ borderRadius: '8px' }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body1" color="error">
                                Video not found or failed to load
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Debug: {videoData ? 'Video data found but no URL' : 'No video data'}
                            </Typography>
                        </Box>
                    );
                })() : (
                    <Box sx={{ p: 2, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Student List Example:</Typography>
                        <List dense>
                            {['John Smith - Grade 5A', 'Emma Johnson - Grade 5A', 'Michael Brown - Grade 5A'].map((student, idx) => (
                                <ListItem key={idx} sx={{ backgroundColor: 'white', mb: 1, borderRadius: 1 }}>
                                    <ListItemIcon><Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>{student[0]}</Avatar></ListItemIcon>
                                    <ListItemText primary={student} />
                                    <CheckIcon color="success" />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )
            },
            video: {
                title: "Tutorial Video",
                content: currentVideoTitle ? (() => {
                    const videoData = getVideoByTitle(currentVideoTitle);
                    console.log('Video URL being used (video case):', videoData?.videoUrl); // Debug log
                    return videoData && videoData.videoUrl ? (
                        <Box sx={{ width: '100%', height: '80vh' }}>
                            <iframe
                                width="100%"
                                height="100%"
                                src={videoData.videoUrl}
                                title={videoData.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                                style={{ borderRadius: '8px' }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body1" color="error">
                                Video not found or failed to load
                            </Typography>
                        </Box>
                    );
                })() : null
            },
            curriculum: {
                title: "Curriculum Structure",
                content: (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Example Curriculum Setup:</Typography>
                        <Card elevation={1} sx={{ p: 2, mb: 2, backgroundColor: theme.palette.primary.light }}>
                            <Typography variant="h6" color="primary">📚 Mathematics</Typography>
                            <List dense sx={{ ml: 2 }}>
                                <ListItem><ListItemText primary="Unit 1: Numbers and Operations" /></ListItem>
                                <ListItem><ListItemText primary="Unit 2: Algebra Basics" /></ListItem>
                                <ListItem><ListItemText primary="Unit 3: Geometry" /></ListItem>
                            </List>
                        </Card>
                    </Box>
                )
            },
            assessment: {
                title: "Assessment Recording",
                content: (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>DOK Level Assessment:</Typography>
                        <Grid container spacing={2}>
                            {[
                                { level: 1, desc: "Recall", color: '#4CAF50' },
                                { level: 2, desc: "Skills", color: '#FF9800' },
                                { level: 3, desc: "Strategic", color: '#2196F3' },
                                { level: 4, desc: "Extended", color: '#9C27B0' }
                            ].map((dok) => (
                                <Grid item xs={6} key={dok.level}>
                                    <Card sx={{ p: 1, textAlign: 'center', backgroundColor: dok.color, color: 'white' }}>
                                        <Typography variant="h6">DOK {dok.level}</Typography>
                                        <Typography variant="body2">{dok.desc}</Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )
            },
            comments: {
                title: "AI Comment Generation",
                content: (
                    <Box sx={{ p: 2, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Generated Comment Example:</Typography>
                        <Paper elevation={2} sx={{ p: 2, backgroundColor: 'white', fontStyle: 'italic' }}>
                            <Typography variant="body2">
                                "Emma has demonstrated excellent progress in Mathematics this term. She consistently achieves DOK Level 3
                                in problem-solving activities and shows strong conceptual understanding. Her strategic thinking skills
                                have improved significantly, particularly in algebraic reasoning..."
                            </Typography>
                        </Paper>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 32, height: 32, backgroundColor: theme.palette.primary.main, mr: 1 }}>🤖</Avatar>
                            <Typography variant="body2" color="text.secondary">Generated by AI in 3 seconds</Typography>
                        </Box>
                    </Box>
                )
            }
        };

        const selectedDemo = demoContent[type];
        return selectedDemo ? selectedDemo.content : null;
    };

    // Dragging handlers
    const handleMouseDown = (event) => {
        if (event.button !== 0) return; // Only left mouse button

        setIsDragging(true);
        const rect = dialogRef.current.getBoundingClientRect();
        setDragOffset({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        });

        event.preventDefault();
    };

    const handleMouseMove = (event) => {
        if (!isDragging) return;

        const newPosition = {
            x: event.clientX - dragOffset.x,
            y: event.clientY - dragOffset.y
        };

        setPosition(newPosition);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Add global mouse event listeners for dragging
    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    const handleExitTutorial = () => {
        if (onComplete) {
            onComplete();
        }
    };

    return (
        <Paper
            ref={dialogRef}
            elevation={8}
            sx={{
                position: 'fixed',
                left: position.x || 50,
                top: position.y || 50,
                maxWidth: isCollapsed ? '200px' : (isMobile ? '95vw' : '1200px'),
                width: isCollapsed ? '200px' : (isMobile ? '95vw' : '1200px'),
                maxHeight: '90vh',
                cursor: isDragging ? 'grabbing' : 'default',
                zIndex: 1400,
                pointerEvents: 'auto',
                overflow: isCollapsed ? 'visible' : 'auto',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
                transition: 'width 0.3s ease, max-width 0.3s ease'
            }}
        >
            <Box sx={{ p: isCollapsed ? 1 : { xs: 2, md: 4 } }}>
                {/* Header - Different for collapsed vs expanded */}
                <Paper
                    elevation={3}
                    sx={{
                        p: isCollapsed ? 2 : 3,
                        mb: isCollapsed ? 0 : 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                >
                    {isCollapsed ? (
                        // Collapsed Header - Simple title and buttons
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                                Gradely Tutorial
                            </Typography>
                            <Stack spacing={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<ExpandIcon />}
                                    onClick={() => setIsCollapsed(false)}
                                    fullWidth
                                    size="small"
                                    sx={{ pointerEvents: 'auto' }}
                                >
                                    Maximize
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<ExitIcon />}
                                    onClick={handleExitTutorial}
                                    fullWidth
                                    size="small"
                                    sx={{ pointerEvents: 'auto' }}
                                >
                                    Exit Tutorial
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        // Expanded Header - Full header with progress
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pointerEvents: 'none' }}>
                                <DragIcon sx={{ mr: 1, color: theme.palette.grey[500], fontSize: 20 }} />
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2, width: 56, height: 56 }}>
                                    <TrophyIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h5" fontWeight="bold" color="primary">
                                        Gradely Quick Start Guide
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Master your classroom management in just 4 simple steps
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        You can minimize or drag the tutorial window while using the dashboard.
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} sx={{ pointerEvents: 'auto' }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"

                                        onClick={() => setIsCollapsed(true)}
                                        size="small"
                                    >
                                        Minimize Tutorial
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<ExitIcon />}
                                        onClick={handleExitTutorial}
                                    >
                                        Exit Tutorial
                                    </Button>
                                </Stack>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Progress</Typography>
                                    <Typography variant="body2" color="text.secondary">{Math.round(progress)}% Complete</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: theme.palette.grey[200],
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: theme.palette.primary.main
                                        }
                                    }}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    icon={<TimelineIcon />}
                                    label={`${completedSteps.size} of ${steps.length} steps completed`}
                                    color="primary"
                                    variant={completedSteps.size === steps.length ? "filled" : "outlined"}
                                />
                                <Chip icon={<TipIcon />} label="Interactive Tutorial" color="secondary" />
                            </Stack>
                        </>
                    )}
                </Paper>

                {!isCollapsed && (
                    // Expanded View - Full tutorial content (only show when not collapsed)
                    <Grid container spacing={3}>
                        {/* Stepper */}
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 2, position: 'sticky', top: 20 }}>


                                <Stepper activeStep={activeStep} orientation="vertical" sx={{ '& .MuiStepLabel-root': { cursor: 'pointer' } }}>
                                    {steps.map((step, index) => (
                                        <Step key={step.label} completed={completedSteps.has(index)} onClick={() => handleStepClick(index)}>
                                            <StepLabel
                                                StepIconComponent={({ active, completed }) => (
                                                    <Badge
                                                        badgeContent={completed ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                                                        sx={{
                                                            '& .MuiBadge-badge': {
                                                                backgroundColor: theme.palette.success.main,
                                                                color: 'white',
                                                                right: -2,
                                                                top: -2
                                                            }
                                                        }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: completed ? theme.palette.success.main :
                                                                    active ? step.color : theme.palette.grey[400],
                                                                width: 40,
                                                                height: 40,
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {step.icon}
                                                        </Avatar>
                                                    </Badge>
                                                )}
                                            >
                                                <Typography variant="subtitle2" fontWeight={activeStep === index ? 700 : 500}>
                                                    {step.label}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Paper>
                        </Grid>

                        {/* Content */}
                        <Grid item xs={12} md={8}>
                            <Fade in={true} key={activeStep}>
                                <Paper elevation={2} sx={{ p: 3, minHeight: '500px' }}>
                                    {activeStep < steps.length && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                <Avatar sx={{
                                                    bgcolor: steps[activeStep].color,
                                                    width: 48,
                                                    height: 48,
                                                    mr: 2
                                                }}>
                                                    {steps[activeStep].icon}
                                                </Avatar>
                                                <Typography variant="h5" fontWeight="bold">
                                                    {steps[activeStep].content.title}
                                                </Typography>
                                            </Box>

                                            <Typography variant="body1" color="text.secondary" mb={3}>
                                                {steps[activeStep].content.description}
                                            </Typography>

                                            {/* Substeps */}
                                            {steps[activeStep].content.substeps && (
                                                <Card elevation={1} sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}>
                                                    <Typography variant="h6" fontWeight="bold" mb={2}>
                                                        📋 Step-by-Step Instructions:
                                                    </Typography>
                                                    <List>
                                                        {(() => {
                                                            let stepNumber = 0;
                                                            return steps[activeStep].content.substeps.map((substep, idx) => (
                                                                <Zoom in={true} key={idx} style={{ transitionDelay: `${idx * 200}ms` }}>
                                                                    {substep.isSubheading ? (
                                                                        <Box sx={{ mt: 2, mb: 1 }}>
                                                                            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ pl: 1 }}>
                                                                                {substep.text}
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <ListItem>
                                                                            <ListItemIcon>
                                                                                <Avatar sx={{
                                                                                    width: 32,
                                                                                    height: 32,
                                                                                    backgroundColor: steps[activeStep].color,
                                                                                    color: 'white'
                                                                                }}>
                                                                                    {++stepNumber}
                                                                                </Avatar>
                                                                            </ListItemIcon>
                                                                            <ListItemText
                                                                                primary={substep.text}
                                                                                sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                                                            />
                                                                            {substep.icon}
                                                                        </ListItem>
                                                                    )}
                                                                </Zoom>
                                                            ));
                                                        })()}
                                                    </List>
                                                </Card>
                                            )}

                                            {/* Tips */}
                                            {steps[activeStep].content.tips && (
                                                <Card elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5dc', borderLeft: `4px solid #d2b48c` }}>
                                                    <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: 'black', fontWeight: 'bold' }}>
                                                        💡 Pro Tips:
                                                    </Typography>
                                                    <List dense>
                                                        {steps[activeStep].content.tips.map((tip, idx) => (
                                                            <ListItem key={idx}>
                                                                <ListItemIcon>
                                                                    <TipIcon sx={{ color: 'black' }} />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={tip}
                                                                    sx={{
                                                                        '& .MuiListItemText-primary': {
                                                                            color: 'black',
                                                                            fontWeight: 'bold'
                                                                        }
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Card>
                                            )}

                                            {/* Interactive Demo Button(s) */}
                                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                                {steps[activeStep].content.actions ? (
                                                    // Multiple buttons for steps with actions array
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                                                        {steps[activeStep].content.actions.map((action, idx) => (
                                                            <Button
                                                                key={idx}
                                                                variant="contained"
                                                                size="large"
                                                                startIcon={<VideoIcon />}
                                                                onClick={() => handleVideoDemo(action.videoTitle)}
                                                                sx={{
                                                                    py: 1.5,
                                                                    px: 3,
                                                                    minWidth: 'auto',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {action.text}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    // Single button for steps with single action
                                                    <Button
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={steps[activeStep].content.videoTitle ? <VideoIcon /> : <PlayIcon />}
                                                        onClick={activeStep === 0 ? handleNext : () => handleDemo(steps[activeStep].content.visual)}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 4
                                                        }}
                                                    >
                                                        {steps[activeStep].content.action}
                                                    </Button>
                                                )}
                                            </Box>

                                            {/* Navigation - Hide on welcome step (step 0) */}
                                            {activeStep !== 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                                                <Button
                                                    disabled={activeStep === 0}
                                                    onClick={handleBack}
                                                    startIcon={<PrevIcon />}
                                                    variant="outlined"
                                                >
                                                    Previous
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <NextIcon />}
                                                    disabled={activeStep === steps.length}
                                                >
                                                    {activeStep === steps.length - 1 ? 'Complete Tutorial' : 'Next Step'}
                                                </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Completion Message */}
                                    {activeStep >= steps.length && (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Zoom in={true}>
                                                <Avatar sx={{
                                                    width: 80,
                                                    height: 80,
                                                    bgcolor: theme.palette.success.main,
                                                    mx: 'auto',
                                                    mb: 3
                                                }}>
                                                    <TrophyIcon sx={{ fontSize: 48 }} />
                                                </Avatar>
                                            </Zoom>
                                            <Typography variant="h4" fontWeight="bold" color="success.main" mb={2}>
                                                🎉 Congratulations!
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary" mb={3}>
                                                You've completed the SmartGrades tutorial. You're now ready to transform your classroom assessment experience!
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="large"
                                                onClick={() => setActiveStep(0)}
                                                sx={{ mr: 2 }}
                                            >
                                                Start Over
                                            </Button>
                                            <Button variant="outlined" size="large">
                                                Go to Dashboard
                                            </Button>
                                        </Box>
                                    )}
                                </Paper>
                            </Fade>
                        </Grid>
                    </Grid>
                )}

                {/* Demo Dialog - Only show when not collapsed */}
                {!isCollapsed && (
                    <Dialog
                        open={showDemo}
                        onClose={() => setShowDemo(false)}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            sx: { borderRadius: 3 }
                        }}
                        sx={{ zIndex: 1500 }}
                    >
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight="bold">
                                {currentDemo && (() => {
                                    if (currentDemo === 'video' && currentVideoTitle) {
                                        return currentVideoTitle;
                                    }
                                    const demoContent = {
                                        welcome: { title: "Gradely Platform Overview" },
                                        students: { title: "Adding Students Tutorial" },
                                        curriculum: { title: "Curriculum Structure" },
                                        assessment: { title: "Assessment Recording" },
                                        comments: { title: "AI Comment Generation" }
                                    };
                                    return demoContent[currentDemo]?.title || "Tutorial";
                                })()}
                            </Typography>
                            <IconButton onClick={() => setShowDemo(false)}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <VisualDemo type={currentDemo} step={steps[activeStep].content} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDemo(false)} variant="contained">
                                Got it!
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Box>
        </Paper>
    );
};

export default InteractiveGettingStartedGuide;